import React from 'react';

/**
 * currentColor icon set for the dark theme. The older icons in this folder
 * hard-code #333333, which disappears on a near-black canvas.
 */
type IconProps = React.SVGAttributes<SVGElement>;

const base = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
};

export function MenuLines({ size = 20, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M3 7h18M3 17h18" />
        </svg>
    );
}

export function CloseX({ size = 20, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M5 5l14 14M19 5L5 19" />
        </svg>
    );
}

export function DownloadArrow({ size = 16, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M12 3v12M7 11l5 5 5-5M4 20h16" />
        </svg>
    );
}

export function ExternalArrow({ size = 14, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M7 17L17 7M9 7h8v8" />
        </svg>
    );
}

export function ChevronLeft({ size = 22, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M15 5l-7 7 7 7" />
        </svg>
    );
}

export function ChevronRight({ size = 22, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M9 5l7 7-7 7" />
        </svg>
    );
}

export function PlayTriangle({ size = 18, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M8 5.5v13a1 1 0 0 0 1.53.85l10-6.5a1 1 0 0 0 0-1.7l-10-6.5A1 1 0 0 0 8 5.5z" />
        </svg>
    );
}

/* Brand marks, single-path so they inherit currentColor cleanly. */

export function GitHubMark({ size = 16, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48l-.01-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.5 9.5 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.35 4.69-4.58 4.93.36.31.68.92.68 1.86l-.01 2.75c0 .27.18.58.69.48A10 10 0 0 0 12 2Z" />
        </svg>
    );
}

export function LinkedInMark({ size = 16, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M4.98 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3 9h4v12H3V9Zm6.5 0h3.8v1.64h.05a4.17 4.17 0 0 1 3.75-2.06c4.01 0 4.75 2.64 4.75 6.07V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.08 1.4-2.08 2.85V21h-4V9Z" />
        </svg>
    );
}

export function QuoteMark({ size = 44, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" fill="currentColor" {...props}>
            <path d="M20 12v8h-5c-1.1 0-2 .9-2 2v2h7v12H4V24c0-6.6 5.4-12 12-12h4Zm24 0v8h-5c-1.1 0-2 .9-2 2v2h7v12H28V24c0-6.6 5.4-12 12-12h4Z" />
        </svg>
    );
}

export function ChevronDown({ size = 16, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...base} {...props}>
            <path d="M5 9l7 7 7-7" />
        </svg>
    );
}

/**
 * A journal with a ribbon. The ribbon drops further when the link is hovered,
 * so give the parent a `group` class.
 */
export function DiaryMark({ size = 17, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
            <path d="M6 4.4A2.4 2.4 0 0 1 8.4 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H8.4A2.4 2.4 0 0 1 6 19.6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M9.5 2.6v18.8" stroke="currentColor" strokeWidth="1.4" opacity="0.45" />
            {/* The ribbon keeps the accent at rest, so the icon is not flat grey, and drops further on hover. */}
            <path d="M16.1 2v6.6l-1.85-1.6-1.85 1.6V2z" fill="var(--accent)" className="origin-top transition-transform duration-300 ease-out group-hover:scale-y-[1.45]" />
        </svg>
    );
}

/** Location pin, the way a journal entry stamps where it happened. */
export function PinMark({ size = 13, ...props }: IconProps & { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
            <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
            <circle cx="12" cy="10" r="2.4" fill="currentColor" />
        </svg>
    );
}
